'use client';

import React from 'react';
import { AlertTriangle, Eye, Clock, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import StatusBadge from '@/components/ui/StatusBadge';

// Backend integration point: GET /api/ehs/incidents/recent?limit=8
const recentItems = [
  {
    id: 'feed-inc-001',
    type: 'incident',
    title: 'Chemical splash — eye exposure',
    dept: 'Chemical Proc.',
    severity: 'critical' as const,
    time: '2h ago',
    incidentId: 'INC-2026-0412',
  },
  {
    id: 'feed-nm-002',
    type: 'near-miss',
    title: 'Forklift near-pedestrian contact',
    dept: 'Logistics',
    severity: 'high' as const,
    time: '5h ago',
    incidentId: 'NM-2026-0389',
  },
  {
    id: 'feed-inc-003',
    type: 'incident',
    title: 'Slip on wet surface — minor laceration',
    dept: 'Maintenance',
    severity: 'medium' as const,
    time: '1d ago',
    incidentId: 'INC-2026-0411',
  },
  {
    id: 'feed-nm-004',
    type: 'near-miss',
    title: 'Valve pressure relief activation',
    dept: 'Operations',
    severity: 'high' as const,
    time: '1d ago',
    incidentId: 'NM-2026-0388',
  },
  {
    id: 'feed-inc-005',
    type: 'incident',
    title: 'Electrical arc flash — no injury',
    dept: 'Electrical',
    severity: 'high' as const,
    time: '2d ago',
    incidentId: 'INC-2026-0410',
  },
  {
    id: 'feed-nm-006',
    type: 'near-miss',
    title: 'Dropped tool from elevated platform',
    dept: 'Construction',
    severity: 'medium' as const,
    time: '3d ago',
    incidentId: 'NM-2026-0387',
  },
];

export default function RecentIncidentsFeed() {
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--border))]">
        <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Recent Activity</h3>
        <Link
          href="/incident-near-miss-analytics"
          className="text-xs text-[hsl(var(--primary))] hover:underline flex items-center gap-0.5"
        >
          View all <ChevronRight size={12} />
        </Link>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-[hsl(var(--border))]">
        {recentItems.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 px-5 py-3.5 hover:bg-[hsl(var(--muted)/0.5)] transition-colors cursor-pointer group"
          >
            <div
              className={`mt-0.5 shrink-0 w-7 h-7 rounded-lg flex items-center justify-center
                ${item.type === 'incident' ?'bg-red-500/12 text-red-500' :'bg-amber-500/12 text-amber-500'
                }`}
            >
              {item.type === 'incident' ? <AlertTriangle size={13} /> : <Eye size={13} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-600 text-[hsl(var(--foreground))] truncate leading-snug">
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">
                  {item.incidentId}
                </span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">·</span>
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{item.dept}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <StatusBadge variant={item.severity} label={item.severity} />
              <span className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-0.5">
                <Clock size={9} />
                {item.time}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}